import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"

import {cn} from "@isudio/utils";

import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../shadcn/tooltip"

type SliderProps = React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
  rangeClassName?: string
  wrapperClassName?: string
  trackClassName?: string
  thumbClassName?: string
  tooltipClassName?: string
  tooltipMode?: "hover" | "always"
  tooltipFormatter?: (value: number) => React.ReactNode
}

const RangeSlider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      value,
      defaultValue = [0],
      rangeClassName,
      wrapperClassName,
      trackClassName,
      thumbClassName,
      tooltipClassName,
      tooltipMode = "hover",
      tooltipFormatter = (val) => val,
      onValueChange,
      ...props
    },
    ref
  ) => {
    // Local state only if uncontrolled
    const [internalValue, setInternalValue] = React.useState<number[]>(
      defaultValue as number[]
    )

    const isControlled = value !== undefined
    const sliderValue = isControlled ? (value as number[]) : internalValue

    const handleChange = (vals: number[]) => {
      if (!isControlled) {
        setInternalValue(vals)
      }
      onValueChange?.(vals)
    }

    return (
      <SliderPrimitive.Root
        ref={ref}
        value={sliderValue}
        onValueChange={handleChange}
        className={cn(
          "relative flex w-full touch-none select-none items-center",
          className
        )}
        {...props}
      >
        <SliderPrimitive.Track
          className={cn(
            "relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/20",
            trackClassName
          )}
        >
          <SliderPrimitive.Range
            className={cn("absolute h-full bg-primary", rangeClassName)}
          />
        </SliderPrimitive.Track>

        {sliderValue.map((val, i) => {
          const thumb = (
            <SliderPrimitive.Thumb
              className={cn(
                "block h-4 w-4 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50",
                thumbClassName
              )}
            />
          )

          return (
            <TooltipProvider key={i}>
              <Tooltip open={tooltipMode === "always" ? true : undefined}>
                <TooltipTrigger asChild>{thumb}</TooltipTrigger>
                <TooltipContent
                  side="top"
                  className={cn("px-2 py-1 text-xs rounded-md", tooltipClassName)}
                >
                  {tooltipFormatter(val)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        })}
      </SliderPrimitive.Root>
    )
  }
)

RangeSlider.displayName = SliderPrimitive.Root.displayName

export { RangeSlider }
